import { useEventEditors } from '@/hooks/useEvents'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { joinNames } from '@/lib/joinNames'
import { requestErrorMessage } from '@/helpers/apiClient'

/** The plan being deleted, as the confirmation describes it. */
export interface PlanToDelete {
  id: string
  label: string
  /** Furniture pieces placed on it. */
  pieces: number
}

interface PlanDeleteDialogProps {
  eventId: string
  /** The plan to delete; null closes the dialog. */
  plan: PlanToDelete | null
  onOpenChange: (open: boolean) => void
  onConfirm: () => void
  busy: boolean
  error: string | null
}

/**
 * Deleting one plan of an event, with what goes with it and who loses it.
 *
 * The trash button used to delete at once: a whole arrangement of hundreds of
 * pieces went on one click, and anyone arranging it at that moment found their
 * room closed under them with nothing to say it was coming.
 *
 * So it waits to know who that is. Until the check answers there is no one to
 * name, and an empty list would read as "nobody"; if it fails, the plan is not
 * deleted on a description that could be leaving someone out.
 */
export function PlanDeleteDialog(props: PlanDeleteDialogProps) {
  const editors = useEventEditors(props.eventId, props.plan !== null)
  const names = editors.data?.plans.find((p) => p.phaseId === props.plan?.id)?.names ?? []

  function describe(): string {
    const plan = props.plan
    if (!plan) return ''
    if (editors.isPending) return 'Checking who has it open…'
    const floor =
      plan.pieces === 0
        ? 'Its floor plan has nothing on it yet.'
        : `Its floor plan goes with it: ${String(plan.pieces)} ${plan.pieces === 1 ? 'piece' : 'pieces'} of furniture.`
    const open =
      names.length > 0 ? ` ${joinNames(names)} ${names.length === 1 ? 'has' : 'have'} it open right now and will lose it.` : ''
    // A plan with furniture on it is kept for ninety days and History puts it back;
    // one with nothing on it is not worth keeping, and is gone.
    const kept =
      plan.pieces === 0
        ? ' This can’t be undone.'
        : ' It is kept for ninety days: History, in the phase bar, puts it back.'
    return `${floor}${open}${kept}`
  }

  return (
    <ConfirmDialog
      open={props.plan !== null}
      onOpenChange={props.onOpenChange}
      title={`Delete “${props.plan?.label ?? 'plan'}”?`}
      description={describe()}
      confirmLabel="Delete plan"
      destructive
      busy={props.busy}
      confirmDisabled={!editors.isSuccess}
      error={
        props.error ??
        (editors.isError ? `Who has it open could not be checked. ${requestErrorMessage(editors.error)}` : null)
      }
      onConfirm={props.onConfirm}
    />
  )
}
